"use client";

import { ChevronLeft, PlayArrow } from "@mui/icons-material";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { generateMediaSrc } from "@v2/components/MediaV2";
import Scrollbar from "@v2/components/Scrollbar";
import VideoStream from "@v2/components/VideoStream";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

interface VideoItem {
    id: number;
    slug: string;
    title: string;
    poster_id: string | null;
    duration: number | null;
    playlist_title: string | null;
}

interface VideoPlaylistLCContent {
    settings: {
        label: string | null;
        title: string | null;
        subtitle: string | null;
        link: string | null;
        link_text: string | null;
    };
    data: VideoItem[];
}

const ITEM_COLORS = [
    '#bb202e', '#d4541c', '#c4870d', '#2e7d32',
    '#1565c0', '#7b1fa2', '#00838f', '#c62828',
];

const formatDuration = (seconds: number | null): string | null => {
    if (!seconds) return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.round(seconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
};

export default function VideoPlaylistLC({ content }: { content: VideoPlaylistLCContent }) {
    const { settings, data: videos } = content;
    const [activeIndex, setActiveIndex] = useState(0);
    // The title belongs to the cover, not to the paused state: it is on screen exactly
    // when the player is showing its poster -- before the first play and again when the
    // video is wound back to the start. Tying it to `paused` instead would drop a title
    // over the frame every time someone stops to scrub.
    const [onCover, setOnCover] = useState(true);
    const playerRef = useRef<HTMLDivElement>(null);
    const [playerHeight, setPlayerHeight] = useState(430);
    const [mounted, setMounted] = useState(false);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    useEffect(() => { setMounted(true); }, []);

    useEffect(() => {
        const el = playerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setPlayerHeight(Math.round(entry.contentRect.height));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    if (!videos?.length) return null;

    const activeVideo = videos[activeIndex];

    const renderPlaylistItem = (video: VideoItem, index: number, opts: { thumbnailW: number; thumbnailH: number; titleFontSize: number; indexFontSize: number; durationFontSize: number; gap: string; p: string; borderRadius: string; flex?: string }) => {
        const isActive = index === activeIndex;
        const color = ITEM_COLORS[index % ITEM_COLORS.length];
        const posterSrc = video.poster_id ? generateMediaSrc(video.poster_id, { width: 200 }) : null;
        const duration = formatDuration(video.duration);

        return (
            <Box
                key={video.id}
                onClick={() => { setActiveIndex(index); setOnCover(true); }}
                sx={{
                    ...(opts.flex ? { flex: opts.flex } : {}),
                    display: 'flex', gap: opts.gap, p: opts.p, borderRadius: opts.borderRadius, cursor: 'pointer',
                    alignItems: 'center',
                    bgcolor: isActive ? `${color}29` : 'rgba(255,255,255,0.04)',
                    border: '1px solid',
                    borderColor: isActive ? `${color}80` : 'transparent',
                    transition: 'background 0.15s, border-color 0.15s',
                    '&:hover': !isActive ? { bgcolor: 'rgba(255,255,255,0.08)' } : {},
                }}
            >
                <Box sx={{
                    flexShrink: 0, width: opts.thumbnailW, height: opts.thumbnailH, borderRadius: '9px',
                    overflow: 'hidden', position: 'relative',
                    background: 'linear-gradient(135deg, #3a3a42, #1c1c20)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {posterSrc ? (
                        <Box
                            component="img"
                            src={posterSrc}
                            alt={video.title}
                            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    ) : (
                        <PlayArrow sx={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.82)', fontSize: isActive ? opts.titleFontSize + 8 : opts.titleFontSize + 6 }} />
                    )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: opts.indexFontSize, fontWeight: 600, color, mb: '3px', lineHeight: 1, letterSpacing: '0.4px' }}>
                        {video.playlist_title ?? String(index + 1).padStart(2, '0')}
                    </Typography>
                    <Typography sx={{
                        fontSize: opts.titleFontSize, fontWeight: 300, lineHeight: 1.4,
                        color: isActive ? '#fff' : '#e9e9ec',
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    }}>
                        {video.title}
                    </Typography>
                    <Typography sx={{ fontSize: opts.durationFontSize, color: '#9a9aa0', mt: '3px' }}>
                        {isActive
                            ? `در حال پخش${duration ? ` · ${duration}` : ''}`
                            : (duration ?? '')}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const playlistItems = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {videos.map((video, index) => renderPlaylistItem(video, index, {
                thumbnailW: 116, thumbnailH: 66, titleFontSize: 13.5,
                indexFontSize: 11, durationFontSize: 11.5, gap: '13px', p: '10px', borderRadius: '14px',
            }))}
        </Box>
    );

    const playlistItemsMobile = (
        <Box sx={{
            display: 'flex', gap: '12px', overflowX: 'auto', pb: '4px',
            scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
            WebkitOverflowScrolling: 'touch',
        }}>
            {videos.map((video, index) => renderPlaylistItem(video, index, {
                flex: '0 0 270px', thumbnailW: 100, thumbnailH: 60, titleFontSize: 12.5,
                indexFontSize: 10, durationFontSize: 10.5, gap: '11px', p: '9px', borderRadius: '13px',
            }))}
        </Box>
    );

    return (
        <Box sx={{ bgcolor: '#16161a' }}>
            <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, lg2: 0 }, py: { xs: '40px', lg: '60px' } }}>

                {/* header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: { xs: '20px', lg: '30px' }, gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Box sx={{ width: 6, height: 34, bgcolor: '#bb202e', borderRadius: '3px', flexShrink: 0 }} />
                        <Box>
                            {settings.title && (
                                <Typography component="h2" sx={{ fontSize: { xs: 20, lg: 28 }, fontWeight: 700, color: '#fff', m: 0, lineHeight: 1.3 }}>
                                    {settings.title}
                                </Typography>
                            )}
                            {settings.subtitle && (
                                <Typography sx={{ mt: '6px', fontSize: { xs: 13, lg: 14.5 }, color: '#a0a0a6' }}>
                                    {settings.subtitle}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {settings.link && (
                        <Box
                            component={Link}
                            href={settings.link}
                            sx={{
                                color: '#fff', fontWeight: 500, fontSize: 14, textDecoration: 'none',
                                border: '1.5px solid rgba(255,255,255,0.25)', px: '22px', py: '11px',
                                borderRadius: '11px', display: 'flex', alignItems: 'center', gap: '8px',
                                '&:hover': { borderColor: 'rgba(255,255,255,0.5)' },
                            }}
                        >
                            {settings.link_text || 'همه‌ی ویدیوها'}
                            <ChevronLeft sx={{ fontSize: 16 }} />
                        </Box>
                    )}
                </Box>

                {/* content grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.85fr 1fr' }, gap: { xs: '16px', lg: '24px' }, alignItems: 'start' }}>

                    {/* main player */}
                    <Box ref={playerRef} sx={{ borderRadius: '20px', overflow: 'hidden', position: 'relative' }}>
                        <VideoStream
                            key={activeVideo.slug}
                            slug={activeVideo.slug}
                            title={activeVideo.title}
                            ratio="16 / 9"
                            poster={null}
                            onStartCoverChange={setOnCover}
                        />
                        <Box sx={{
                            position: 'absolute', bottom: 0, right: 0, left: 0,
                            px: '16px', pt: '48px', pb: '14px',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.78), transparent)',
                            zIndex: 4,
                            opacity: onCover ? 1 : 0,
                            transition: 'opacity 0.3s ease',
                            pointerEvents: 'none',
                        }}>
                            <Typography sx={{ fontSize: { xs: 15, lg: 16 }, fontWeight: 300, color: '#fff', lineHeight: 1.4 }}>
                                {activeVideo.title}
                            </Typography>
                        </Box>
                    </Box>

                    {/* playlist */}
                    {mounted ? (
                        isDesktop ? (
                            <Scrollbar
                                style={{ height: playerHeight }}
                                trackYProps={{ style: { right: 0, left: 'auto', width: '3px' } }}
                                sx={{
                                    '& .ScrollbarsCustom-Track': {
                                        background: 'rgba(255,255,255,0.08) !important',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        '& .ScrollbarsCustom-Thumb': {
                                            background: 'rgba(255,255,255,0.35) !important',
                                            borderRadius: '2px !important',
                                        },
                                    },
                                    '&:hover .ScrollbarsCustom-Track': {
                                        opacity: 1,
                                    },
                                    '& .ScrollbarsCustom-Content': {
                                        pl: '10px',
                                    },
                                }}
                            >
                                {playlistItems}
                            </Scrollbar>
                        ) : playlistItemsMobile
                    ) : playlistItems}
                </Box>
            </Box>
        </Box>
    );
}
